import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { FileInput } from '../components/ui/FileInput';
import { MockBadge } from '../components/MockBadge';
import { StepUpField } from '../components/StepUpField';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { maskEmail, parseCsv, type ParsedCsv } from '../lib/csv';
import { stepUpBody, stepUpCodeReady, stepUpErrorText, useStepUpWindow } from '../lib/stepUp';
import type { CsvImportReport, CsvInviteResult, CsvInviteStatus, MailingList } from '../types/api';

const CHUNK_SIZE = 50;
/** More rows than this in one file: split the file (each chunk is one request). */
const MAX_ROWS = 10_000;
const MAX_LABELS_PER_ROW = 10;

type Target = 'email' | 'firstName' | 'lastName' | 'language' | 'country' | 'labels';
const TARGETS: { key: Target; label: string; hints: RegExp }[] = [
  { key: 'email', label: 'Email', hints: /^(e-?mail|email address|mail)$/i },
  { key: 'firstName', label: 'First name', hints: /^(first ?name|fornavn|vorname)$/i },
  { key: 'lastName', label: 'Last name', hints: /^(last ?name|efternavn|nachname)$/i },
  { key: 'language', label: 'Language', hints: /^(language|sprog|sprache)$/i },
  { key: 'country', label: 'Country', hints: /^(country|land)$/i },
  { key: 'labels', label: 'Tags (separated by ; or |)', hints: /^(tags?|labels?|mærker)$/i },
];

type Mapping = Record<Target, number>;

function guessMapping(headers: string[]): Mapping {
  const out = {} as Mapping;
  for (const t of TARGETS) out[t.key] = headers.findIndex((h) => t.hints.test(h.trim()));
  return out;
}

const REPORT_KEYS: { key: keyof CsvImportReport; label: string }[] = [
  { key: 'received', label: 'Rows read' },
  { key: 'created', label: 'Created' },
  { key: 'updated', label: 'Updated (gaps filled)' },
  { key: 'unchanged', label: 'Unchanged' },
  { key: 'skippedInvalidEmail', label: 'Skipped: invalid email' },
  { key: 'skippedSuppressed', label: 'Skipped: suppressed' },
  { key: 'skippedDuplicateInChunk', label: 'Skipped: duplicate in file' },
  { key: 'invitable', label: 'Can get a confirmation email' },
  { key: 'notMailable', label: 'Stored but not subscribed' },
  { key: 'labelsApplied', label: 'Tags applied' },
  { key: 'labelsRefused', label: 'Tags refused' },
  { key: 'languagesRefused', label: 'Languages not recognised' },
  { key: 'countriesRefused', label: 'Countries not recognised' },
];

function addReports(a: CsvImportReport, b: CsvImportReport): CsvImportReport {
  const out = { ...a };
  for (const { key } of REPORT_KEYS) out[key] = a[key] + b[key];
  return out;
}

function emptyReport(): CsvImportReport {
  const out = {} as CsvImportReport;
  for (const { key } of REPORT_KEYS) out[key] = 0;
  return out;
}

/** One id per import run; ties the invite mails to this import. Matches the API's pattern. */
function newImportId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `imp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Admin only: import contacts from a CSV file. The default is "not subscribed". */
export function KontakterImportPage() {
  const { isAdmin } = useAuth();
  const stepUp = useStepUpWindow();
  const [csv, setCsv] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [invite, setInvite] = useState(false);
  const [source, setSource] = useState('');
  const [listId, setListId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ dryRun: boolean; report: CsvImportReport } | null>(null);
  const [importId, setImportId] = useState<string | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteDone, setInviteDone] = useState<{
    sent: number;
    failed: number;
    skipped: number;
  } | null>(null);
  const queryClient = useQueryClient();

  const inviteStatus = useQuery({
    queryKey: ['csv-invite-status', importId],
    queryFn: ({ signal }) =>
      apiFetch<CsvInviteStatus>(
        `/contacts/import/invite/status?importId=${encodeURIComponent(importId ?? '')}`,
        { signal },
      ),
    enabled: isAdmin && importId !== null,
  });

  const lists = useQuery({
    queryKey: ['lists'],
    queryFn: ({ signal }) => apiFetch<{ items: MailingList[] }>('/lists', { signal }),
    enabled: isAdmin,
  });

  if (!isAdmin) return <Alert>Only administrators can import contacts.</Alert>;

  async function onFile(file: File | undefined) {
    setResult(null);
    setError(null);
    setImportId(null);
    setConfirmSend(false);
    setInviteDone(null);
    setInviteError(null);
    if (!file) return;
    const parsed = parseCsv(await file.text());
    if (parsed.headers.length === 0 || parsed.rows.length === 0) {
      setCsv(null);
      setMapping(null);
      setError('The file has no rows.');
      return;
    }
    if (parsed.rows.length > MAX_ROWS) {
      setCsv(null);
      setMapping(null);
      setError(`The file has more than ${MAX_ROWS} rows. Split it into smaller files.`);
      return;
    }
    setCsv(parsed);
    setMapping(guessMapping(parsed.headers));
  }

  function mappedRows() {
    if (!csv || !mapping) return [];
    const cell = (row: string[], key: Target): string | undefined => {
      const idx = mapping[key];
      const v = idx >= 0 ? row[idx]?.trim() : undefined;
      return v ? v : undefined;
    };
    return csv.rows.map((row) => {
      const labels = cell(row, 'labels')
        ?.split(/[;|]/)
        .map((l) => l.trim().toLowerCase())
        .filter(Boolean)
        .slice(0, MAX_LABELS_PER_ROW);
      const firstName = cell(row, 'firstName');
      const lastName = cell(row, 'lastName');
      const language = cell(row, 'language');
      const country = cell(row, 'country');
      return {
        email: cell(row, 'email') ?? '',
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
        ...(language ? { language } : {}),
        ...(country ? { country } : {}),
        ...(labels && labels.length > 0 ? { labels } : {}),
      };
    });
  }

  const consentReady = !invite || (source.trim().length > 0 && listId !== '');
  const mapped = mapping !== null && mapping.email >= 0;
  const needsCode = !stepUp.active;

  async function run(dryRun: boolean) {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const rows = mappedRows();
      const runImportId = invite && !dryRun ? newImportId() : null;
      const consent =
        invite && runImportId
          ? { mode: 'invite' as const, source: source.trim(), listId, importId: runImportId }
          : invite
            ? { mode: 'invite' as const, source: source.trim(), listId, importId: newImportId() }
            : { mode: 'none' as const };
      let total = emptyReport();
      let sentCode = false;
      for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
        // Only the first real chunk carries the code; the 30-minute window covers the rest.
        const auth =
          !dryRun && !sentCode && needsCode && stepUpCodeReady(code) ? stepUpBody(code) : {};
        const res = await apiFetch<{ report: CsvImportReport }>('/contacts/import', {
          method: 'POST',
          body: { rows: rows.slice(i, i + CHUNK_SIZE), consent, dryRun, ...auth },
        });
        if ('totpCode' in auth || 'recoveryCode' in auth) {
          sentCode = true;
          stepUp.onStepUpSuccess();
          setCode('');
        }
        total = addReports(total, res.report);
        // Kept as soon as one chunk landed: if a later chunk fails, the
        // contacts already imported can still be invited.
        if (runImportId) setImportId(runImportId);
      }
      setResult({ dryRun, report: total });
      setInviteDone(null);
      setConfirmSend(false);
      setImportId(runImportId);
    } catch (err) {
      stepUp.onStepUpError(err);
      setError(
        stepUpErrorText(err) ??
          (err instanceof Error && err.message
            ? err.message
            : 'The import failed. Rows before the failure may already be imported.'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function sendInvites() {
    if (!importId) return;
    setInviteError(null);
    setSending(true);
    try {
      let sent = 0;
      let failed = 0;
      let skipped = 0;
      let sentCode = false;
      for (let guard = 0; guard < 500; guard += 1) {
        const auth = !sentCode && needsCode && stepUpCodeReady(code) ? stepUpBody(code) : {};
        const res = await apiFetch<CsvInviteResult>('/contacts/import/invite', {
          method: 'POST',
          body: { importId, ...auth },
        });
        if ('totpCode' in auth || 'recoveryCode' in auth) {
          sentCode = true;
          stepUp.onStepUpSuccess();
          setCode('');
        }
        sent += res.sent;
        failed += res.failed;
        skipped += res.skipped;
        if (res.remaining <= 0) break;
      }
      setInviteDone({ sent, failed, skipped });
    } catch (err) {
      stepUp.onStepUpError(err);
      setInviteError(
        stepUpErrorText(err) ??
          (err instanceof Error && err.message
            ? err.message
            : 'Sending stopped. Check the counts and try again.'),
      );
    } finally {
      setSending(false);
      setConfirmSend(false);
      void queryClient.invalidateQueries({ queryKey: ['csv-invite-status', importId] });
    }
  }

  const invitable = inviteStatus.data?.counts.invitable ?? 0;
  const invitesEnabled = inviteStatus.data?.enabled ?? false;

  const previewRows = mappedRows().slice(0, 5);

  return (
    <>
      <header className="flex items-center gap-3">
        <Link to="/kontakter" className="no-underline">
          ← Contacts
        </Link>
        <MockBadge />
      </header>
      <h1 className="m-0 text-2xl font-semibold tracking-tight">Import contacts from CSV</h1>
      <p className="m-0 text-sm text-muted">
        The file is read in your browser and sent in chunks of {CHUNK_SIZE} rows. Existing contacts
        only get empty fields filled; their consent is never changed.
      </p>

      <Card className="p-4 flex flex-col gap-3">
        <Label htmlFor="csv-file">CSV file</Label>
        <FileInput
          id="csv-file"
          accept=".csv,text/csv"
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
      </Card>

      {csv && mapping ? (
        <>
          <Card className="p-4 flex flex-col gap-3">
            <h2 className="m-0 text-sm font-semibold">Match the columns</h2>
            <div className="grid grid-cols-2 max-[700px]:grid-cols-1 gap-3">
              {TARGETS.map((t) => (
                <div key={t.key} className="flex flex-col gap-1.5">
                  <Label htmlFor={`map-${t.key}`}>{t.label}</Label>
                  <select
                    id={`map-${t.key}`}
                    className="h-9 rounded-md border border-border bg-card px-2 text-sm"
                    value={mapping[t.key]}
                    onChange={(e) => setMapping({ ...mapping, [t.key]: Number(e.target.value) })}
                  >
                    <option value={-1}>
                      {t.key === 'email' ? 'Choose a column' : 'Not in file'}
                    </option>
                    {csv.headers.map((h, i) => (
                      <option key={`${h}-${i}`} value={i}>
                        {h || `Column ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {!mapped ? <Alert>Choose which column holds the email address.</Alert> : null}
          </Card>

          {mapped ? (
            <Card className="p-4 flex flex-col gap-2">
              <h2 className="m-0 text-sm font-semibold">
                Preview ({csv.rows.length} rows, email addresses hidden)
              </h2>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left text-muted">
                    <th className="py-1 font-medium">Email</th>
                    <th className="py-1 font-medium">First name</th>
                    <th className="py-1 font-medium">Language</th>
                    <th className="py-1 font-medium">Country</th>
                    <th className="py-1 font-medium">Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r, i) => (
                    <tr key={i} className="border-t border-border-soft">
                      <td className="py-1 font-mono">{maskEmail(r.email)}</td>
                      <td className="py-1">{r.firstName ? `${r.firstName[0]}…` : '—'}</td>
                      <td className="py-1">{r.language ?? '—'}</td>
                      <td className="py-1">{r.country ?? '—'}</td>
                      <td className="py-1">{r.labels ? r.labels.join(', ') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}

          <Card className="p-4 flex flex-col gap-3">
            <h2 className="m-0 text-sm font-semibold">Consent</h2>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="consent-mode"
                checked={!invite}
                onChange={() => setInvite(false)}
              />
              <span>
                Not subscribed (default). The contacts are stored but receive no newsletters.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="consent-mode"
                checked={invite}
                onChange={() => setInvite(true)}
              />
              <span>
                They agreed elsewhere: send a confirmation email. Nobody is subscribed by the
                import. Each person gets one email and is subscribed only when they click the link.
              </span>
            </label>
            {invite ? (
              <div className="grid grid-cols-2 max-[700px]:grid-cols-1 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="consent-source">Where they agreed (source)</Label>
                  <Input
                    id="consent-source"
                    value={source}
                    maxLength={200}
                    placeholder="e.g. Trade fair signup sheet, Oct 2026"
                    onChange={(e) => setSource(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="consent-list">Confirmation email is for the list</Label>
                  <select
                    id="consent-list"
                    className="h-9 rounded-md border border-border bg-card px-2 text-sm"
                    value={listId}
                    onChange={(e) => setListId(e.target.value)}
                  >
                    <option value="">Choose a list</option>
                    {(lists.data?.items ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}
          </Card>

          {mapped ? (
            <Card className="p-4 flex flex-col gap-3">
              {needsCode ? (
                <StepUpField id="import-code" value={code} onChange={setCode} />
              ) : (
                <p className="m-0 text-xs text-muted">
                  Your 2FA window is open ({stepUp.minutesLeft} min left). No code needed.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  disabled={busy || !consentReady}
                  onClick={() => void run(true)}
                >
                  {busy ? 'Working…' : 'Check the file (dry run)'}
                </Button>
                <Button
                  disabled={busy || !consentReady || (needsCode && !stepUpCodeReady(code))}
                  onClick={() => void run(false)}
                >
                  {busy ? 'Working…' : 'Import'}
                </Button>
              </div>
            </Card>
          ) : null}
        </>
      ) : null}

      {error ? <Alert>{error}</Alert> : null}

      {result ? (
        <Card className="p-4 flex flex-col gap-2">
          <h2 className="m-0 text-sm font-semibold">
            {result.dryRun ? 'Dry run: nothing was saved' : 'Import finished'}
          </h2>
          <dl className="m-0 grid grid-cols-2 max-[700px]:grid-cols-1 gap-x-6 gap-y-1 text-sm">
            {REPORT_KEYS.map(({ key, label }) => (
              <div key={key} className="flex justify-between gap-4">
                <dt className="text-muted">{label}</dt>
                <dd className="m-0" data-testid={`report-${key}`}>
                  {result.report[key]}
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      ) : null}

      {result && !result.dryRun && importId ? (
        <Card className="p-4 flex flex-col gap-3">
          <h2 className="m-0 text-sm font-semibold">Confirmation emails</h2>
          <p className="m-0 text-sm" data-testid="invitable-count">
            {inviteStatus.isPending ? 'Checking…' : `${invitable} can be invited.`}
          </p>
          {inviteStatus.data && !invitesEnabled ? (
            <Alert>
              Sending confirmation emails is switched off for this company. It stays off until an
              administrator switches it on, so nothing can be sent yet.
            </Alert>
          ) : null}
          {inviteDone ? (
            <Alert>
              Sent {inviteDone.sent}, failed {inviteDone.failed}, skipped {inviteDone.skipped}.
            </Alert>
          ) : null}
          {inviteError ? <Alert>{inviteError}</Alert> : null}
          {confirmSend ? (
            <div className="flex flex-col gap-2">
              <p className="m-0 text-sm" role="alert">
                This sends {invitable} confirmation {invitable === 1 ? 'email' : 'emails'} now. Each
                address gets one email, ever. Continue?
              </p>
              <div className="flex gap-2">
                <Button disabled={sending} onClick={() => void sendInvites()}>
                  {sending ? 'Sending…' : `Yes, send ${invitable}`}
                </Button>
                <Button
                  variant="secondary"
                  disabled={sending}
                  onClick={() => setConfirmSend(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button
                disabled={
                  !invitesEnabled ||
                  invitable === 0 ||
                  sending ||
                  (needsCode && !stepUpCodeReady(code))
                }
                onClick={() => setConfirmSend(true)}
              >
                Send confirmation emails
              </Button>
              {needsCode && invitesEnabled && invitable > 0 ? (
                <p className="m-0 mt-2 text-xs text-muted">
                  Enter your 2FA code above to enable the button.
                </p>
              ) : null}
            </div>
          )}
        </Card>
      ) : null}
    </>
  );
}
